import React, { useState, useEffect } from 'react';
import classes from './UploadAllowanceModal.module.css';
import api, { withCompanyId } from '../../Components/API/Api';
import Swal from 'sweetalert2';

function UploadAllowanceModal({ isOpen, onClose }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [allowanceType, setAllowanceType] = useState('');
  const [allowanceTypes, setAllowanceTypes] = useState([]);
  const [fetchingTypes, setFetchingTypes] = useState(false);

  // Fetch allowance types when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchAllowanceTypes();
    }
  }, [isOpen]);

  const fetchAllowanceTypes = async () => {
    try {
      setFetchingTypes(true);
      const res = await api.get('/v1/get-allowance-type');
      if (res.data && Array.isArray(res.data)) {
        setAllowanceTypes(res.data);
      } else if (res.data?.data && Array.isArray(res.data.data)) {
        setAllowanceTypes(res.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch allowance types', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load allowance types. Please try again.',
        confirmButtonColor: '#16A34A',
      });
    } finally {
      setFetchingTypes(false);
    }
  };

  if (!isOpen) return null;

  const handleDownload = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const res = await api.get('/v1/download_direct_allowance_template', {
        params: { token },
        responseType: 'blob',
      });

      const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'allowance_template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error', err);
      Swal.fire({
        icon: 'error',
        title: 'Download Failed',
        text: 'Failed to download template. Please try again.',
        confirmButtonColor: '#16A34A',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files?.[0] || null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      Swal.fire({
        icon: 'warning',
        title: 'Action Required',
        text: 'Please choose a file to upload',
        confirmButtonColor: '#16A34A',
      });
      return;
    }

    if (!allowanceType) {
      Swal.fire({
        icon: 'warning',
        title: 'Action Required',
        text: 'Please select an allowance type',
        confirmButtonColor: '#16A34A',
      });
      return;
    }

    try {
      setLoading(true);
      const fd = new FormData();
      fd.append('file', file, file.name);
      fd.append('allowance_type', allowanceType);
      const payload = withCompanyId(fd);

      const res = await api.post('/v1/salary_allowance/upload_direct', payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (res.status === 200 || res.status === 201) {
        Swal.fire({
          icon: 'success',
          title: 'Upload Successful',
          text: res.data?.message || 'Allowance data uploaded successfully!',
          confirmButtonColor: '#16A34A',
        });
        setFile(null);
        setAllowanceType('');
        onClose();
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Upload Failed',
          text: 'An error occurred during upload. Please try again.',
          confirmButtonColor: '#16A34A',
        });
      }
    } catch (err) {
      console.error('Upload error', err);
      Swal.fire({
        icon: 'error',
        title: 'Upload Failed',
        text: err.response?.data?.message || 'Failed to upload file. Please try again.',
        confirmButtonColor: '#16A34A',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={classes.overlay}>
      <div className={classes.modal}>
        <h3>Upload Allowance</h3>
        <p>Download the template, fill it, then upload the completed Excel file.</p>

        <div className={classes.formGroup}>
          <label htmlFor="allowanceType" className={classes.label}>Allowance Type *</label>
          <select
            id="allowanceType"
            value={allowanceType}
            onChange={(e) => setAllowanceType(e.target.value)}
            className={classes.select}
            disabled={fetchingTypes}
          >
            <option value="">
              {fetchingTypes ? '-- Loading --' : '-- Select Allowance Type --'}
            </option>
            {allowanceTypes.map((type, index) => {
              const id = type.id || type.allowance_id || type.allowanceId;
              const description = type.description;
              return (
                <option key={id || index} value={id || description}>
                  {description}
                </option>
              );
            })}
          </select>
        </div>

        <div className={classes.actions}>
          <button
            onClick={handleDownload}
            disabled={loading}
            className={`${classes.btn} ${classes.btnPrimary}`}
          >
            Download Template
          </button>
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            className={classes.fileInput}
          />
        </div>

        <div className={classes.buttonGroup}>
          <button
            onClick={onClose}
            className={`${classes.btn} ${classes.btnSecondary}`}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !file || !allowanceType}
            className={`${classes.btn} ${classes.btnPrimary}`}
          >
            {loading ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default UploadAllowanceModal;
